/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import React, { FormEvent, useEffect, useMemo, useState, FC } from "react";
import {
  AppAlert,
  LaddaButton,
  ErrorMessageValidator,
  PasswordRequirementTooltip,
} from "@advapacs/common";
import { FormLabel } from "react-bootstrap";
import { IRootState } from "../../config/reducers";
import getUpdatePasswordValidator from "../../utils/validator";
import { useAppDispatch } from "../../config/store";
import { logout } from "../../pages/login/ducks/operations";
import { ProfileAction } from "./ducks/profileSlice";
import { updatePassword } from "./ducks/operators";

interface IUpdatePasswordForm {
  password: string;
  confirmPassword: string;
  currentPassword: string;
}

const defaultPass = {
  currentPassword: "",
  password: "",
  confirmPassword: "",
};

interface PasswordProps {
  isModal?: boolean;
  hasPassword?: boolean;
  cancel?: () => void;
}

const Password: FC<PasswordProps> = ({
  isModal = false,
  hasPassword = true,
  cancel,
}) => {
  const { t } = useTranslation("admin");
  const dispatch = useAppDispatch();
  const { loading, errorMessage, updatedPassword } = useSelector(
    (state: IRootState) => state.profileReducer
  );

  const [, setError] = useState({});

  const { account } = useSelector((state: IRootState) => state.loginReducer);
  const [pass, setPass] = useState<IUpdatePasswordForm>(defaultPass);

  const validator = useMemo(() => {
    return getUpdatePasswordValidator(t);
  }, [t]);

  const savePassword = (e: FormEvent) => {
    e.preventDefault();
    if (!validator.allValid()) {
      validator.showMessages();
      setError({});
      return;
    }
    validator.hideMessages();
    dispatch(updatePassword(pass));
  };

  useEffect(() => {
    if (updatedPassword && !(isModal && hasPassword)) {
      dispatch(logout());
    }
  }, [updatedPassword, isModal, hasPassword]);
  const AlertSection = () => {
    return (
      <>
        {account?.forcePasswordChange && (
          <AppAlert.SimpleAlert
            message={t("login:password_change_required")}
            dismissible={false}
            variant="warning"
            className="m-b-xs"
          />
        )}
        {updatedPassword && (
          <AppAlert.SimpleAlert
            variant="success"
            message={t("user.password_updated")}
            className="m-b-xs"
          />
        )}
        <AppAlert.SimpleAlert
          variant="info"
          className="m-b-xs"
          message={
            !hasPassword
              ? t("user.clear_set_own_session_message")
              : t("user.clear_own_session_message")
          }
        />
        {errorMessage && (
          <AppAlert.ErrorAlert
            className="m-b-xs"
            onClose={() => dispatch(ProfileAction.clearError())}
            error={errorMessage}
            dismissible
          />
        )}
      </>
    );
  };

  return (
    <>
      <div>
        {AlertSection()}
        {hasPassword && (
          <div className="form-group row">
            <FormLabel
              className={`col-lg-${isModal ? "12" : "3"} col-form-label`}
            >
              {t("user.current_password")}
              <span className="text-danger">*</span>
            </FormLabel>
            <div className={`col-lg-${isModal ? "12" : "4"}`}>
              <input
                value={pass.currentPassword}
                type="password"
                className="form-control"
                onChange={(e) =>
                  setPass({
                    ...pass,
                    currentPassword: e.target.value,
                  })
                }
                autoComplete="new-password"
              />
              <ErrorMessageValidator
                errorMessage={validator.message(
                  t("user.current_password"),
                  pass.currentPassword,
                  "required"
                )}
              />
            </div>
          </div>
        )}

        <div className="form-group row">
          <FormLabel
            className={`col-lg-${isModal ? "12" : "3"} col-form-label`}
          >
            {t("user.password")}
            <span className="text-danger">*</span>
          </FormLabel>
          <div className={`col-lg-${isModal ? "12" : "4"}`}>
            <PasswordRequirementTooltip>
              <input
                value={pass.password}
                type="password"
                className="form-control"
                onChange={(e) =>
                  setPass({
                    ...pass,
                    password: e.target.value,
                  })
                }
                autoComplete="new-password"
              />
            </PasswordRequirementTooltip>
            <ErrorMessageValidator
              errorMessage={validator.message(
                `password`,
                pass.password,
                "required|passwordPattern"
              )}
            />
          </div>
        </div>
        <div className="form-group row">
          <FormLabel
            className={`col-lg-${isModal ? "12" : "3"} col-form-label`}
          >
            {t("user.confirm_password")}
            <span className="text-danger">*</span>
          </FormLabel>
          <div className={`col-lg-${isModal ? "12" : "4"}`}>
            <input
              value={pass.confirmPassword}
              type="password"
              className="form-control"
              onChange={(e) =>
                setPass({
                  ...pass,
                  confirmPassword: e.target.value,
                })
              }
              autoComplete="new-password"
            />
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("user.confirm_password"),
                pass.confirmPassword,
                "required"
              )}
            />
            <ErrorMessageValidator
              errorMessage={validator.message(``, pass, "comparePassword")}
            />
          </div>
        </div>
        {!isModal && (
          <div className="form-group row">
            <span className="col-form-label col-lg-3" />
            <div className={`col-lg-${isModal ? "12" : "4"}`}>
              <div className="form-check form-check-switch form-check-switch-left">
                <LaddaButton
                  btnName={t("button.update_password")}
                  className="btn-primary"
                  onClickHandler={savePassword}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        )}

        {isModal && (
          <div className="form-group row flex-center m-b-none">
            <LaddaButton
              btnName={
                hasPassword
                  ? t("button.update_password")
                  : t("user_setting.set_password")
              }
              className="btn-primary btn-confirm"
              onClickHandler={savePassword}
              loading={loading}
            />

            <button
              style={{ width: 150 }}
              className="btn btn-secondary m-l-sm btn-confirm"
              onClick={cancel}
            >
              {t("cancel")}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Password;
